import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PolicyModal } from '../components/PolicyModal';
import { FloatingCTA } from '../components/FloatingCTA';
type PolicyType = 'privacy' | 'terms' | 'refund';
export function LegSwellingAdvertorial() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-2184">
      {/* Header */}
      <header className="bg-white border-b border-bone-dark" data-id="element-2185">
        <div className="max-w-4xl mx-auto px-4 py-2" data-id="element-2186">
          <Link to="/" className="flex items-center" data-id="element-2187">
            <img src="https://cdn.magicpatterns.com/uploads/483EwVYaDBT86YSQWmyKfo/GINEXT_LOGO.png" alt="GINEXT" className="h-6 w-auto" loading="eager" data-id="element-2188" />
          </Link>
        </div>
      </header>

      {/* Article Container */}
      <article className="max-w-3xl mx-auto px-4 py-6" data-id="element-2189">
        {/* Article Header */}
        <div className="mb-6" data-id="element-2190">
          <h1 className="text-2xl md:text-3xl font-bold text-charcoal leading-tight mb-3" data-id="element-2191">
            I Thought My Legs Were Just Tired — Until I Realized They Were
            Holding Pressure
          </h1>

          <p className="text-sm text-charcoal-light mb-3" data-id="element-2192">
            By late afternoon, my legs didn't hurt exactly.
          </p>

          <p className="text-sm text-charcoal-light mb-3" data-id="element-2193">
            They just felt… heavier.
          </p>
        </div>

        {/* Article Content */}
        <div className="prose prose-sm max-w-none" data-id="element-2194">
          <div className="space-y-3 text-charcoal leading-relaxed" data-id="element-2195">
            <p className="text-sm" data-id="element-2196">
              Shoes fit tighter. Ankles looked thicker. When I took my socks
              off, the marks stayed longer than they used to.
            </p>

            <p className="text-sm" data-id="element-2197">
              I told myself it was normal. Long days. Sitting too much. Getting
              older.
            </p>

            <p className="text-sm font-semibold" data-id="element-2198">
              But here's what bothered me most:
            </p>

            <p className="text-sm" data-id="element-2199">No pain cream ever helped.</p>

            <p className="text-sm" data-id="element-2200">
              Cold gels. Menthol rubs. "Extra strength" formulas. They all did
              the same thing — made my skin feel numb, then wore off.
            </p>

            <p className="text-sm font-semibold" data-id="element-2201">The heaviness stayed.</p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2202">
              Why Swelling Feels So Hard to Fix
            </h2>

            <p className="text-sm" data-id="element-2203">Most people treat leg swelling like pain.</p>

            <p className="text-sm font-semibold" data-id="element-2204">
              But swelling isn't a sensation problem. It's a movement problem.
            </p>

            <p className="text-sm" data-id="element-2205">
              When circulation slows — from long days, inactivity, stress, or
              time — fluid doesn't move the way it should.
            </p>

            <p className="text-sm" data-id="element-2206">So it stays.</p>

            <p className="text-sm" data-id="element-2207">
              That's why numbing creams don't work. They don't move anything.
              They just distract the nerves.
            </p>

            <p className="text-sm" data-id="element-2208">
              And compression helps… but only while it's on.
            </p>

            <p className="text-sm font-semibold" data-id="element-2209">
              I wanted something that worked with my body — not against it.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2210">
              The Moment That Changed How I Thought About Relief
            </h2>

            <p className="text-sm" data-id="element-2211">A friend said something simple:</p>

            <div className="bg-bone/50 border-l-4 border-ginger p-3 rounded-r-lg my-4" data-id="element-2212">
              <p className="text-base font-bold text-charcoal m-0" data-id="element-2213">
                "Cold shuts things down. Warmth invites things to move."
              </p>
            </div>

            <p className="text-sm" data-id="element-2214">That stuck.</p>

            <p className="text-sm" data-id="element-2215">
              Our bodies associate warmth with circulation. With relaxation.
              With release.
            </p>

            <p className="text-sm" data-id="element-2216">
              So instead of trying another freezing gel, I tried something
              completely different — a botanical oil designed to create gentle,
              controlled warmth.
            </p>

            <p className="text-sm" data-id="element-2217">
              Not heat. Not burn. Just enough warmth to signal circulation.
            </p>

            <p className="text-sm font-semibold" data-id="element-2218">That's when things changed.</p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2219">
              What I Noticed First
            </h2>

            <p className="text-sm" data-id="element-2220">There was no shock.</p>

            <p className="text-sm" data-id="element-2221">No icy blast. No aggressive tingle.</p>

            <p className="text-sm" data-id="element-2222">
              Just a slow warmth that spread instead of attacking.
            </p>

            <p className="text-sm" data-id="element-2223">
              And over time — not instantly, but consistently — my legs felt
              lighter.
            </p>

            <p className="text-sm" data-id="element-2224">Not numb. Not masked.</p>

            <p className="text-sm font-semibold" data-id="element-2225">Supported.</p>

            <p className="text-sm" data-id="element-2226">
              The tightness eased. The end-of-day heaviness didn't build the
              same way.
            </p>

            <p className="text-sm font-semibold" data-id="element-2227">
              For the first time, relief didn't feel like a trick.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2228">
              Why This Works When Creams Don't
            </h2>

            <div className="mb-4 rounded-lg overflow-hidden shadow-lg" data-id="element-2229">
              <img src="https://cdn.magicpatterns.com/uploads/miu3veA8hGZj2xhtF4swUi/u8896349413_An_educational_anatomical_illustration_showing_mu_40b13d86-9c0d-4e08-a9b7-380274aef750_3.png" alt="Anatomical illustration showing circulation" className="w-full h-auto" loading="lazy" data-id="element-2230" />
            </div>

            <p className="text-sm" data-id="element-2231">
              Most topical products are designed to block sensation.
            </p>

            <p className="text-sm font-semibold" data-id="element-2232">This oil works differently.</p>

            <p className="text-sm" data-id="element-2233">
              It uses specific botanicals — like ginger and black pepper — known
              for encouraging circulation through warmth.
            </p>

            <p className="text-sm" data-id="element-2234">
              Not synthetic heat. Not artificial numbing.
            </p>

            <p className="text-sm" data-id="element-2235">
              Just a signal the body already understands.
            </p>

            <div className="bg-bone/50 border-l-4 border-ginger p-3 rounded-r-lg my-4" data-id="element-2236">
              <p className="text-base font-bold text-charcoal m-0" data-id="element-2237">
                When circulation improves, fluid can move again. When fluid
                moves, pressure can release.
              </p>
            </div>

            <p className="text-sm" data-id="element-2238">That's the part most products ignore.</p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2239">
              "This Doesn't Feel Like Pain Cream"
            </h2>

            <p className="text-sm" data-id="element-2240">
              That's the phrase I keep hearing from people who try it.
            </p>

            <p className="text-sm" data-id="element-2241">
              Because it doesn't feel like something overpowering your body.
            </p>

            <p className="text-sm font-semibold" data-id="element-2242">
              It feels like something giving your body permission to do what it
              already knows how to do.
            </p>

            <p className="text-sm" data-id="element-2243">People use it on:</p>

            <ul className="space-y-1 my-3" data-id="element-2244">
              <li className="text-sm" data-id="element-2245">Ankles and feet that swell by evening</li>
              <li className="text-sm" data-id="element-2246">Lower legs that feel heavy or tight</li>
              <li className="text-sm" data-id="element-2247">
                Knees and joints that feel stiff after long days
              </li>
              <li className="text-sm" data-id="element-2248">
                Areas where cold products just don't make sense anymore
              </li>
            </ul>

            <p className="text-sm" data-id="element-2249">
              And because it absorbs cleanly, there's no greasy residue, no
              overpowering scent, and no waiting around.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2250">
              The Bigger Shift
            </h2>

            <p className="text-sm" data-id="element-2251">
              What surprised me most wasn't just how my legs felt.
            </p>

            <p className="text-sm" data-id="element-2252">It was how my mindset changed.</p>

            <p className="text-sm" data-id="element-2253">I stopped thinking:</p>

            <p className="text-sm italic" data-id="element-2254">"How do I shut this feeling off?"</p>

            <p className="text-sm" data-id="element-2255">And started thinking:</p>

            <p className="text-sm italic" data-id="element-2256">"How do I support my body better?"</p>

            <p className="text-sm font-semibold" data-id="element-2257">
              That's a very different relationship with relief.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2258">
              The Product Behind the Experience
            </h2>

            <div className="mb-4 rounded-lg overflow-hidden shadow-lg" data-id="element-2259">
              <img src="https://cdn.magicpatterns.com/uploads/rCeUo34cwDxa2NpcRLnUx1/GINEXT_BOTTLE_.png" alt="GINEXT Ginger Oil bottle" className="w-full h-auto" loading="lazy" data-id="element-2260" />
            </div>

            <p className="text-sm" data-id="element-2261">The oil is called GINEXT.</p>

            <p className="text-sm" data-id="element-2262">
              It's a 100% natural botanical formulation designed to:
            </p>

            <ul className="space-y-1 my-3" data-id="element-2263">
              <li className="text-sm" data-id="element-2264">Create deep, controlled warmth</li>
              <li className="text-sm" data-id="element-2265">
                Support circulation beneath the surface
              </li>
              <li className="text-sm" data-id="element-2266">Avoid numbing or freezing sensations</li>
            </ul>

            <p className="text-sm" data-id="element-2267">
              No menthol overload. No harsh chemicals. No gimmicks.
            </p>

            <p className="text-sm" data-id="element-2268">
              Just something that fits into real life — morning or evening,
              daily or as needed.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2269">
              If This Sounds Familiar
            </h2>

            <p className="text-sm" data-id="element-2270">If your legs:</p>

            <ul className="space-y-1 my-3" data-id="element-2271">
              <li className="text-sm" data-id="element-2272">Feel heavier as the day goes on</li>
              <li className="text-sm" data-id="element-2273">Look more swollen by evening</li>
              <li className="text-sm" data-id="element-2274">Don't respond to cold creams</li>
              <li className="text-sm" data-id="element-2275">Feel tight rather than "painful"</li>
            </ul>

            <p className="text-sm font-semibold" data-id="element-2276">
              This approach may make more sense than what you've already tried.
            </p>

            <div className="bg-white border border-bone-dark rounded-lg p-3 my-4" data-id="element-2277">
              <p className="text-base italic text-charcoal m-0" data-id="element-2278">
                "This doesn't feel like pain cream. It feels like my body
                letting go."
              </p>
            </div>

            {/* CTA Section */}
            <div className="bg-bone rounded-xl p-4 my-6 text-center" data-id="element-2279">
              <h3 className="text-lg font-bold text-charcoal mb-2" data-id="element-2280">
                Learn how GINEXT supports natural circulation and leg comfort
              </h3>
              <div className="flex flex-col sm:flex-row gap-2 justify-center items-center mt-3" data-id="element-2281">
                <Link to="/#bundles" data-id="element-2282">
                  <motion.button whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} className="bg-ginger text-white px-5 py-2.5 rounded-lg font-bold text-sm shadow-warm hover:shadow-warm-hover transition-all duration-300 inline-flex items-center gap-2" data-id="element-2283">
                    See How GINEXT Works
                    <ArrowRight className="w-4 h-4" data-id="element-2284" />
                  </motion.button>
                </Link>
              </div>

              <p className="text-xs text-charcoal-light mt-3 italic" data-id="element-2285">
                30-day money-back guarantee. No pressure.
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-charcoal text-gray-400 py-4 mt-8 text-center text-xs" data-id="element-2286">
        <div className="max-w-4xl mx-auto px-4" data-id="element-2287">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-2288">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-2289">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-2290">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-2291">
              Refund Policy
            </button>
          </div>
          <p data-id="element-2292">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-2293">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-2294" />

      <FloatingCTA ctaText="Support Natural Leg Circulation" ctaLink="/#bundles" subText="Warming botanical relief • 30-Day Guarantee" data-id="element-2295" />
    </div>;
}